import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { CreativeElementApi } from "../api/creative-element-api";
import { CreativeElementCategory } from "@repo/api/creative-element";
import { queryClient } from "./query-client";
import Toast from "../components/Toast";

export type ElementsFilterParams = {
  category?: CreativeElementCategory;
  tags?: string[];
};

type CreativeElementCursor =
  | { createdAt: string; externalId: string }
  | undefined;

export function usePagedCreativeElementsQuery(
  teamExternalId: string,
  filterParams: ElementsFilterParams,
  pageSize: number,
) {
  return useInfiniteQuery({
    queryKey: creativeElementQueryKeys.paged(
      teamExternalId,
      filterParams,
      pageSize,
    ),
    queryFn: ({ pageParam }) => {
      return CreativeElementApi.getPagedCreativeElements(teamExternalId, {
        ...filterParams,
        pageSize,
        cursorCreatedAt: pageParam?.createdAt,
        cursorExternalId: pageParam?.externalId,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as CreativeElementCursor,
    meta: {
      errorMessage: "Failed to fetch creative elements",
    },
  });
}

export function usePinnedCreativeElementsQuery(
  teamExternalId: string,
  elementExternalIds: string[],
  filterParams: ElementsFilterParams,
) {
  return useQuery({
    queryKey: creativeElementQueryKeys.pinned(
      teamExternalId,
      filterParams,
      elementExternalIds,
    ),
    queryFn: () =>
      CreativeElementApi.getPagedCreativeElements(teamExternalId, {
        elementExternalIds,
        ...filterParams,
        pageSize: null,
      }),
    enabled: elementExternalIds.length > 0,
    meta: {
      errorMessage: "Failed to fetch pinned creative elements",
    },
  });
}

export function useUploadCreativeElementMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationFn: async (files: File[]) => {
      return await Promise.all(
        files.map((file) =>
          CreativeElementApi.uploadCreativeElement(teamExternalId, file),
        ),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: creativeElementQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to upload element");
    },
  });
}

export function useDeleteCreativeElementMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationFn: (elementExternalId: string) =>
      CreativeElementApi.deleteCreativeElement(
        teamExternalId,
        elementExternalId,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: creativeElementQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to delete element");
    },
  });
}

export const creativeElementQueryKeys = {
  all: () => ["creativeElement"],
  paged: (
    teamExternalId: string,
    filterParams: ElementsFilterParams,
    pageSize: number,
  ) => [
    "creativeElement",
    "paged",
    { teamExternalId, ...filterParams, pageSize },
  ],
  pinned: (
    teamExternalId: string,
    filterParams: ElementsFilterParams,
    elementExternalIds: string[],
  ) => [
    "creativeElement",
    "pinned",
    { teamExternalId, ...filterParams, elementExternalIds },
  ],
};
